import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Collapse,
  Fade,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Custom Icon Configuration
// Using the actual bee logo provided by the user
import beeIcon from '../assets/buzzlator.png';

// Styled components
const ChatbotContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 10020, // Higher than all tooltips and icons
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const ChatWindow = styled(Paper)(({ theme }) => ({
  width: 400,
  maxHeight: 600,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows[8],
  borderRadius: 12,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flex: 1,
  maxHeight: 400,
  overflowY: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const MessageBubble = styled(Box)(({ theme, isUser }) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
}));

const MessageContent = styled(Paper)(({ theme, isUser }) => ({
  maxWidth: '80%',
  padding: theme.spacing(1.5),
  backgroundColor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: 12,
  wordWrap: 'break-word',
}));

const BuzzwordChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    security: false,
    crisis: false,
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    
    // Basic frontend content filtering
    const inappropriatePatterns = [
      /\b(fuck|shit|bitch|asshole|dick|pussy|cunt|motherfucker|bastard)\b/i,
      /\b(scheiße|arschloch|fotze|schlampe|wichser|schwuchtel|hurensohn)\b/i,
      /\b(kill|murder|suicide|bomb|terrorist|nazi|hitler)\b/i,
      /\b(töten|mord|selbstmord|bombe|terrorist|nazi)\b/i,
    ];
    
    for (const pattern of inappropriatePatterns) {
      if (pattern.test(userMessage)) {
        setError('Bitte verwenden Sie angemessene Sprache für technische Begriffe.');
        return;
      }
    }
    
    // Check for spam patterns
    const spamPatterns = [
      /(.)\1{4,}/, // Repeated characters
      /^[a-zA-Z]{15,}$/, // Long random strings
      /\b(buy|sell|click|free|money|cash|loan|credit)\b/i,
      /\b(kaufen|verkaufen|klick|kostenlos|geld|kredit)\b/i,
    ];
    
    for (const pattern of spamPatterns) {
      if (pattern.test(userMessage)) {
        setError('Diese Art von Anfrage wird nicht unterstützt.');
        return;
      }
    }
    
    setInputValue('');
    setError(null);

    // Add user message
    const newUserMessage = {
      id: Date.now(),
      text: userMessage,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newUserMessage]);

    // Add loading message
    const loadingMessage = {
      id: Date.now() + 1,
      text: 'Analysiere Buzzword...',
      isUser: false,
      isLoading: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, loadingMessage]);
    setIsLoading(true);

    try {
      const response = await fetch('/api/v1/buzzword/explain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': await getCsrfToken(),
        },
        body: JSON.stringify({
          term: userMessage,
          audience: 'Anfänger',
          locale: 'de',
          eli5: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Remove loading message and add response
      setMessages(prev => prev.filter(msg => !msg.isLoading));
      
      const botMessage = {
        id: Date.now() + 2,
        text: data.simple_explanation,
        isUser: false,
        timestamp: new Date(),
        data: data, // Store full response data
      };
      setMessages(prev => [...prev, botMessage]);

      // Dispatch custom event to notify other components that a new buzzword was explained
      const buzzwordExplainedEvent = new CustomEvent('buzzwordExplained', {
        detail: {
          term: data.term,
          timestamp: new Date(),
          data: data
        }
      });
      console.log('Dispatching buzzwordExplained event:', buzzwordExplainedEvent.detail);
      window.dispatchEvent(buzzwordExplainedEvent);

    } catch (err) {
      console.error('Error sending message:', err);
      setError('Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      
      // Remove loading message
      setMessages(prev => prev.filter(msg => !msg.isLoading));
    } finally {
      setIsLoading(false);
    }
  };

  const getCsrfToken = async () => {
    try {
      const response = await fetch('/api/v1/csrf-token');
      const data = await response.json();
      return data.csrf_token;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return '';
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderMessage = (message) => {
    if (message.isLoading) {
      return (
        <MessageBubble isUser={false}>
          <MessageContent isUser={false}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">{message.text}</Typography>
            </Box>
          </MessageContent>
        </MessageBubble>
      );
    }

    if (message.isUser) {
      return (
        <MessageBubble isUser={true}>
          <MessageContent isUser={true}>
            <Typography variant="body2">{message.text}</Typography>
          </MessageContent>
        </MessageBubble>
      );
    }

    // Bot message with buzzword data
    if (message.data) {
      const { data } = message;
      return (
        <MessageBubble isUser={false}>
          <MessageContent isUser={false}>
            <Box>
              <Typography variant="h6" gutterBottom>
                🔎 {data.term}
                {data.cached && (
                  <Typography component="span" variant="caption" sx={{ 
                    ml: 1, 
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}>
                    (aus Cache)
                  </Typography>
                )}
              </Typography>
              
              <Typography variant="body2" gutterBottom>
                ✅ {data.one_sentence}
              </Typography>
              
              <Typography variant="body2" paragraph>
                {data.simple_explanation}
              </Typography>

              {data.analogy && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  🪄 <strong>Analogy:</strong> {data.analogy}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" gutterBottom>
                🧪 <strong>Example:</strong> {data.example}
              </Typography>

              {/* Security Relevance */}
              <Box mt={2}>
                <Button
                  size="small"
                  startIcon={<SecurityIcon />}
                  endIcon={expandedSections.security ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => toggleSection('security')}
                  variant="outlined"
                  fullWidth
                >
                  🛡️ Sicherheitsrelevanz ({data.security_relevance.info_sec_relevance_score}/5)
                </Button>
                <Collapse in={expandedSections.security}>
                  <Box mt={1} pl={2}>
                    <Typography variant="body2" gutterBottom>
                      {data.security_relevance.summary}
                    </Typography>
                    <Chip 
                      label={`Bewertung: ${data.security_relevance.info_sec_relevance_score}/5`}
                      size="small"
                      color={data.security_relevance.info_sec_relevance_score >= 3 ? 'error' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      <strong>Abschwächung:</strong> {data.security_relevance.mitigation}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>

              {/* Crisis Relevance */}
              <Box mt={1}>
                <Button
                  size="small"
                  startIcon={<WarningIcon />}
                  endIcon={expandedSections.crisis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => toggleSection('crisis')}
                  variant="outlined"
                  fullWidth
                >
                  🚨 Krisenmanagement ({data.crisis_relevance.crisis_mgmt_relevance_score}/5)
                </Button>
                <Collapse in={expandedSections.crisis}>
                  <Box mt={1} pl={2}>
                    <Typography variant="body2" gutterBottom>
                      {data.crisis_relevance.summary}
                    </Typography>
                    <Chip 
                      label={`Bewertung: ${data.crisis_relevance.crisis_mgmt_relevance_score}/5`}
                      size="small"
                      color={data.crisis_relevance.crisis_mgmt_relevance_score >= 3 ? 'error' : 'default'}
                    />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      <strong>Aktion:</strong> {data.crisis_relevance.playbook_action}
                    </Typography>
                  </Box>
                </Collapse>
              </Box>

              {data.related_terms && data.related_terms.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    🔗 <strong>Verwandte Begriffe:</strong> {data.related_terms.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </MessageContent>
        </MessageBubble>
      );
    }

    // Simple bot message
    return (
      <MessageBubble isUser={false}>
        <MessageContent isUser={false}>
          <Typography variant="body2">{message.text}</Typography>
        </MessageContent>
      </MessageBubble>
    );
  };

  return (
    <ChatbotContainer>
      {/* Chat Window */}
      <Collapse in={isOpen} direction="up">
        <ChatWindow>
          <ChatHeader>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                component="img"
                src={beeIcon}
                alt=""
                sx={{
                  width: 40,
                  height: 40,
                  filter: 'brightness(0) invert(1)', // Make it white for the header
                }}
              />
              <Typography variant="h6">Buzzword-Übersetzer</Typography>
            </Box>
            <IconButton
              color="inherit"
              onClick={() => setIsOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <ChatBody>
            {messages.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  Hallo, ich bin Buzzlator
                </Typography>
                <Box
                  component="img"
                  src={beeIcon}
                  alt=""
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 2,
                    opacity: 0.7,
                  }}
                />
                <Typography variant="h6" gutterBottom>
                  Brauchst du Hilfe beim Verstehen eines Buzzwords oder Fachjargons?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gib einen technischen Begriff oder ein Buzzword ein, und ich erkläre es dir in einfacher Sprache mit Kontext zu Sicherheit und Krisenmanagement.
                </Typography>
              </Box>
            )}

            {messages.map(renderMessage)}

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}

            <div ref={messagesEndRef} />
          </ChatBody>

          <ChatInput>
            <Box display="flex" gap={1}>
              <TextField
                ref={inputRef}
                fullWidth
                size="small"
                placeholder="Gib ein Buzzword zur Erklärung ein..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                variant="outlined"
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <Box textAlign="center" mt={1}>
              <Typography variant="caption" color="text.secondary">
              Copyright © cyberlagebild.de
              </Typography>
            </Box>
          </ChatInput>
        </ChatWindow>
      </Collapse>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 10020,
          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
          },
        }}
      >
        <Box
          component="img"
          src={beeIcon}
          alt="Buzzword-Übersetzer"
          sx={{
            width: 44,
            height: 44,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
          }}
        />
      </Fab>
    </ChatbotContainer>
  );
};

export default BuzzwordChatbot; 